'use client';

import { Heading, Body } from '@depop/web-thrift-ui/Typography';
import { LoadingBall } from '@depop/web-ui-kit/LoadingBall';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';

import { BrowseProductCard } from '../BrowseProductCard';

import styles from './styles.module.css';

import { InfiniteScroll } from '@/components/InfiniteScroll';
import { RQ_SEARCH_EXPLORE } from '@/modules/ReactQuery/cacheKeys';
import { getExploreProducts } from '@/modules/explore/api';
import { useCurrentLocation } from '@/modules/location/useCurrentLocation';
import { getNextPageParam } from '@/modules/pagination/getNextPageParam';
import { getObjectsFromPages } from '@/modules/pagination/getObjectsFromPages';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

export function EmptyResultsList() {
  const { location } = useCurrentLocation();
  const t = useTranslations('search');
  const [isAuth] = useCurrentUser();

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteQuery({
      queryKey: [`${RQ_SEARCH_EXPLORE}_${location}`],
      queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
        getExploreProducts(
          {
            limit: String(24),
            after: pageParam,
            include_like_count: 'true',
          },
          isAuth
        ),
      initialPageParam: undefined,
      getNextPageParam,
      refetchOnMount: false,
    });

  const products = getObjectsFromPages(data);

  return (
    <>
      <div className={styles.emptyListTitleContainer}>
        <Heading variant="md">{t('NoResultsHeading')}</Heading>
        <Body variant="sm-regular" className={styles.emptyListBodyText}>
          {t('NoResultsBody')}
        </Body>
        <Heading variant="md" className={styles.emptyListSubHeading}>
          {t('NoResultsSubHeading')}
        </Heading>
      </div>
      {isLoading ? (
        <LoadingBall />
      ) : (
        <InfiniteScroll
          onEndReached={fetchNextPage}
          hasMore={hasNextPage}
          loading={isFetchingNextPage}
          LoadingComponent={
            <div className={styles.loaderWrapper}>
              <LoadingBall />
            </div>
          }
        >
          <ol className={styles.emptyProductList}>
            {products &&
              products.map((product) => {
                return (
                  <li className={styles.listItem} key={product.slug}>
                    <BrowseProductCard
                      product={product}
                      id={product.id}
                      slug={product.slug}
                      /**
                       * TODO (https://depopmarket.atlassian.net/browse/WEB-8566):
                       * Add isBoosted and brandName and fix grid layout.
                       * Previously these weren't returned from webapi explore endpoint
                       * but now they are and it messes up the grid layout.
                       */
                      // isBoosted={product.is_boosted}
                      // brandName={product.brand_name}
                      attributes={product.attributes}
                      pricing={product.pricing}
                      sizes={product.sizes}
                      pictures={product.pictures}
                      isLiked={product.is_liked}
                    />
                  </li>
                );
              })}
          </ol>
        </InfiniteScroll>
      )}
    </>
  );
}
